'use client';

import { useProductPrice } from '@eliadmin/core/src/components/dlz/product-detail/buy-box/use-product-price';
import { Badge } from '@eliadmin/core/src/components/dlz/ui/badge';
import { ProductWithDimensionsDetailPrice } from '@eliadmin/core/src/components/product/detail/product-with-dimensions-detail-price';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { hasDimensions } from '@eliadmin/core/src/utils/product-utils';

/** Figma „Price block“: price, struck-through price, savings badge and the VAT note. */
function PriceView() {
  const { t } = useTranslations();
  const price = useProductPrice();

  return (
    <div className="flex flex-col gap-2">
      {price.retail ? <p className="dlz-body-s text-[#6B7079]">{price.retail}</p> : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className={cn('dlz-h2', price.previous ? 'text-[#D7191F]' : 'text-[#0F1012]')}>
          {price.current}
        </p>
        {price.previous ? (
          <del className="dlz-price-old text-[#6B7079]">{price.previous}</del>
        ) : null}
        {price.saving ? <Badge tone="sale">Ušetríte {price.saving}</Badge> : null}
      </div>
      <p className="dlz-caption text-[#6B7079]">{price.vatNote}</p>
      {price.packs ? (
        <dl className="dlz-body-s mt-2 flex flex-col gap-2 text-[#0F1012]">
          <div className="flex justify-between gap-4 font-semibold">
            <dt>{price.packs.content}</dt>
            <dd>{price.packs.contentPrice}</dd>
          </div>
          <div className="dlz-body-m-medium flex justify-between gap-4 border-t border-[#E2E4E7] pt-2 text-[#D7191F]">
            <dt>{t('text23')}</dt>
            <dd>{price.packs.total}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}

/**
 * Price area of a sellable product. Until a variant is chosen it tells so (core texts); products
 * with dimensions keep the core price component, which owns the dimension inputs and the
 * multiplied price.
 */
export function PriceBlock() {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  if (!selectedVariant && product.variants?.length) {
    const allChosen = selectedValues.length === product.attributes?.length;
    return <p className="dlz-h4 text-[#0F1012]">{t(allChosen ? 'text495' : 'text496')}</p>;
  }

  return hasDimensions(product.attributes) ? <ProductWithDimensionsDetailPrice /> : <PriceView />;
}
